import { Injectable } from '@angular/core';

export type BrowserPermission = 'default' | 'granted' | 'denied';

/**
 * The single seam onto the browser Notification API. Nothing else in the app may
 * reference `Notification` directly, so the permission prompt has exactly one
 * reachable call site and there is one place to stub.
 */
@Injectable({ providedIn: 'root' })
export class BrowserNotificationPort {
  get supported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  get permission(): BrowserPermission {
    if (!this.supported) {
      return 'denied';
    }
    return window.Notification.permission as BrowserPermission;
  }

  async requestPermission(): Promise<BrowserPermission> {
    if (!this.supported) {
      return 'denied';
    }
    try {
      return (await window.Notification.requestPermission()) as BrowserPermission;
    } catch {
      // deprecated callback-only implementations reject here; treat as a refusal
      return 'denied';
    }
  }

  /** This tab is the one the user is looking at right now. */
  get visible(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'visible';
  }

  /** This tab's window holds OS focus. Visible is not enough — another window may be in front. */
  get focused(): boolean {
    return typeof document !== 'undefined' && document.hasFocus();
  }

  /**
   * Null rather than a throw: an unsupported platform, a refused permission or a
   * constructor TypeError must never reach the caller.
   */
  show(title: string, options?: NotificationOptions): Notification | null {
    if (!this.supported || this.permission !== 'granted') {
      return null;
    }
    try {
      return new window.Notification(title, options);
    } catch {
      console.warn('[BrowserNotificationPort] the notification could not be shown');
      return null;
    }
  }
}
